import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Textarea } from '../../../../components/inputs/Textarea';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdNumber, TdSeamless, TdSelect } from '../../../../components/inline-add-table/cells';
import { ApiError } from '../../../../lib/apiError';
import { itemIdentityOrName } from '../../../../lib/itemIdentity';
import { focusValidationError } from '../../../../lib/validationFocus';
import { requisitionSlipsApi } from '../requisitionSlipsApi';
import type {
  CurrentRequester,
  RequestableItem,
  RequestableItemType,
  RequisitionSlipDetail,
  RequisitionSlipLineInput,
  RequisitionSlipLinePreference,
} from '../requisitionSlipTypes';
import { LINE_PREFERENCE, LINE_PREFERENCE_LABEL } from '../requisitionSlipTypes';
import { sampleRequisitionSlip } from '../utils/sampleRequisitionSlip';

const ID_PREFIX = 'requisition-slip';

// The requester's own words. Ordered as the column reads: the broader ask first.
const PREFERENCE_OPTIONS = [LINE_PREFERENCE.type, LINE_PREFERENCE.item].map((value) => ({
  value: String(value),
  label: LINE_PREFERENCE_LABEL[value],
}));

/**
 * Raise or correct a requisition slip.
 *
 * **ONE PANEL, not a stepper.** What is left after the office and the accountable employee are
 * derived away is a purpose and the lines — a rail over that would promise stages of work that
 * are two fields, which is the call that took the Identity step off the item form.
 *
 * **THE REQUESTING OFFICE IS STATED AND THE ACCOUNTABLE EMPLOYEE IS NOT SHOWN AT ALL.** Both are
 * derived server-side from the requester's own personnel record, and the request carries no
 * field for either: a value a caller can name is a value a caller can misname onto a signed
 * slip. The accountable employee is always the requester, so a box saying "you" told the reader
 * nothing (product call, 03 Nov 2026). There is no "Store asked" — the store is settled at
 * fulfilment.
 *
 * **A LINE STATES A PREFERENCE — Type or Item** (product call, 03 Nov 2026): "a laptop" or
 * "that laptop". The cell beside it IS the answer, so it swaps with the preference — a type
 * picker on a Type line, the item picker on an Item line — and the value it held CLEARS when
 * the preference moves, because the request carries exactly one of the two ids.
 */
export function RequisitionSlipModal({
  editing,
  onClose,
  onSaved,
}: {
  /** The slip being corrected, or undefined to raise one. */
  editing?: RequisitionSlipDetail;
  onClose: () => void;
  onSaved: (saved: RequisitionSlipDetail) => void;
}) {
  const isEdit = editing !== undefined;

  const [purpose, setPurpose] = useState(editing?.purpose ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  /*
    NORMALISE AT THE SEAM, where a wire row becomes a form row. A nullable field arrives as
    `undefined` rather than null, so `?? null` here is what makes the row type's promise true
    and keeps every comparison downstream from having to know about the serializer.
  */
  const [rows, setRows] = useState<RequisitionSlipLineInput[]>(
    editing?.items.map((line) => ({
      preference: line.preference,
      // ONE id per line: the type is the server's to stamp on an Item line, so it is not sent.
      inventoryItemTypeId: line.preference === LINE_PREFERENCE.type ? line.inventoryItemTypeId : null,
      inventoryItemId: line.inventoryItemId ?? null,
      requestedQuantity: line.requestedQuantity,
      remarks: line.remarks ?? null,
    })) ?? [],
  );

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const [items, setItems] = useState<RequestableItem[]>([]);
  const [itemTypes, setItemTypes] = useState<RequestableItemType[]>([]);
  const [requester, setRequester] = useState<CurrentRequester | null>(null);

  /*
    SEEDED TRUE: the feeds start in an effect that runs after paint, so `false` would flash the
    real form — an empty office line and an empty grid — before the skeleton.
  */
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    /*
      THE FEEDS FAIL SOFT. A failed picker load leaves the form working and the rest of the
      page intact rather than failing the whole dialog — the repository share dialog's own
      call. A failure here is not a failure of the SAVE path, so it must not reach the banner
      the save uses, and `focusValidationError` is deliberately not called from this catch.
    */
    void Promise.allSettled([
      requisitionSlipsApi.requestableItems(controller.signal).then(setItems),
      requisitionSlipsApi.requestableItemTypes(controller.signal).then(setItemTypes),
      requisitionSlipsApi.me(controller.signal).then(setRequester),
    ]).then(() => {
      if (!controller.signal.aborted) setLoading(false);
    });

    return () => controller.abort();
  }, []);

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const itemTypesById = useMemo(() => new Map(itemTypes.map((t) => [t.id, t])), [itemTypes]);

  const itemTypeOptions = useMemo(
    () => itemTypes.map((t) => ({ value: t.id, label: t.name })),
    [itemTypes],
  );

  const itemOptions = useMemo(
    () =>
      items.map((i) => ({
        value: i.id,
        /*
          THE LABEL IS THE PRODUCT, through `lib/itemIdentity` — an item has no name column of
          its own, so a catalog holding three laptops would otherwise offer "Laptop" three
          times. The SKU leaves the label and stays SEARCHABLE.
        */
        label: itemIdentityOrName(i, i.itemName ?? i.sku),
        sublabel: i.itemName ?? undefined,
        keywords: i.sku,
      })),
    [items],
  );

  const update = useCallback((index: number, patch: Partial<RequisitionSlipLineInput>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }, []);

  // A row counts once it names what its preference asks for.
  const liveRows = rows.filter((row) =>
    row.preference === LINE_PREFERENCE.type ? row.inventoryItemTypeId != null : row.inventoryItemId != null,
  );

  const canSave = purpose.trim().length > 0 && liveRows.length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const body = {
        purpose: purpose.trim(),
        notes: notes.trim() || null,
        items: rows,
      };

      const saved = isEdit
        ? await requisitionSlipsApi.update(editing.id, body)
        : await requisitionSlipsApi.create(body);

      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this slip.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit ${editing.slipNumber}` : 'Raise a requisition slip'}
      size="3xl"
      id={`${ID_PREFIX}-modal`}
      loading={loading}
      confirm={{
        id: `${ID_PREFIX}-confirm`,
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      autogenerate={{
        onGenerate: () => {
          const generated = sampleRequisitionSlip({ items, itemTypes });

          /*
            NEVER A FIELD THE READER HAS ALREADY DECIDED — a line already picked keeps its
            preference, type or item, quantity and remark. The generator fills what is empty.
          */
          if (!purpose.trim()) setPurpose(generated.purpose);
          if (!notes.trim()) setNotes(generated.notes);
          if (liveRows.length === 0) setRows(generated.items);
        },
        disabled: saving || (items.length === 0 && itemTypes.length === 0),
      }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          THE OFFICE IS STATED, and an account with no post is told so HERE rather than meeting
          a refusal on save. It fails SOFT: the message comes back on the DTO, so the rest of
          the form still works and a reader can see what they would have asked for.
        */}
        {requester?.error != null ? (
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">{requester.error}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-600">
            Raised for{' '}
            <strong className="text-slate-800">{requester?.orgUnitName ?? 'your office'}</strong>,
            read from your own employee record. It is printed on the slip and is where the
            supplies are delivered.
          </p>
        )}

        <InlineAddTable
          headers={[
            'Preference *',
            'Type / Item *',
            'Unit',
            { label: 'Available', align: 'right' },
            { label: 'Qty *', align: 'right' },
            'Remarks',
          ]}
          colWidths={[130, 320, 80, 110, 100, 200]}
          // v3: Category gave way to Preference, and the Item column swaps with it (03 Nov 2026).
          resizeKey={`${ID_PREFIX}-lines-v3`}
          rowCount={rows.length}
          onAddRow={() =>
            setRows((current) => [
              ...current,
              {
                // Item is SEEDED — the grid's long-standing ask; Type is one pick away.
                preference: LINE_PREFERENCE.item,
                inventoryItemTypeId: null,
                inventoryItemId: null,
                requestedQuantity: 1,
                remarks: null,
              },
            ])
          }
          addDisabled={saving}
          addButtonId={`${ID_PREFIX}-add-line`}
          footerContent={
            /*
              SHORT, because the footer sits inside the grid's own horizontal scroll region and
              a long sentence is CLIPPED rather than wrapped.

              IT SAYS THE TWO RULES A READER WILL ASSUME THE OTHER WAY: a line need not name the
              exact item, and asking for more than is on the shelf is a supported submission.
            */
            <p className="text-xs text-slate-600">
              Choose Type to ask for any item of that type. More than the available figure is
              fine — the shortfall is what Logistics restocks against.
            </p>
          }
        >
          {rows.map((row, index) => {
            const asksForType = row.preference === LINE_PREFERENCE.type;
            const pickedItem = row.inventoryItemId != null ? itemsById.get(row.inventoryItemId) : undefined;
            const pickedType =
              row.inventoryItemTypeId != null ? itemTypesById.get(row.inventoryItemTypeId) : undefined;

            /*
              THE ADVISORY FIGURE FOLLOWS WHAT WAS ASKED: the item's own stock, or the whole
              type's on a Type line. Neither is a cap.
            */
            const available = asksForType ? pickedType?.availableQuantity : pickedItem?.availableQuantity;

            return (
              <InlineRow
                key={index}
                idx={index + 1}
                onRemove={() => setRows((current) => current.filter((_, i) => i !== index))}
              >
                <TdSelect
                  id={`${ID_PREFIX}-items.${index}.preference`}
                  ariaLabel={`Preference on line ${index + 1}`}
                  value={String(row.preference)}
                  /*
                    BOTH ids CLEAR when the preference moves — the cell beside it is swapping to
                    a different question, and the request carries exactly one of the two.
                  */
                  onChange={(next) => {
                    const preference = Number(next) as RequisitionSlipLinePreference;
                    if (preference === row.preference) return;
                    update(index, { preference, inventoryItemTypeId: null, inventoryItemId: null });
                  }}
                  options={PREFERENCE_OPTIONS}
                  sort="none"
                  disabled={saving}
                />

                {asksForType ? (
                  <TdSelect
                    // The key REMOUNTS the cell, so a half-typed search does not carry across.
                    key="type"
                    id={`${ID_PREFIX}-items.${index}.inventoryItemTypeId`}
                    ariaLabel={`Type on line ${index + 1}`}
                    value={row.inventoryItemTypeId}
                    onChange={(next) => update(index, { inventoryItemTypeId: next || null })}
                    options={itemTypeOptions}
                    remote
                    disabled={saving}
                  />
                ) : (
                  <TdSelect
                    key="item"
                    id={`${ID_PREFIX}-items.${index}.inventoryItemId`}
                    ariaLabel={`Item on line ${index + 1}`}
                    value={row.inventoryItemId}
                    onChange={(next) => update(index, { inventoryItemId: next || null })}
                    options={itemOptions}
                    remote
                    disabled={saving}
                  />
                )}

                {/* The unit is a LIVE view off the item; a type alone has none. */}
                <TdSeamless align="left">{asksForType ? '—' : (pickedItem?.unitOfMeasure ?? '—')}</TdSeamless>

                <TdSeamless align="right">{available != null ? String(available) : '—'}</TdSeamless>

                <TdNumber
                  id={`${ID_PREFIX}-items.${index}.requestedQuantity`}
                  ariaLabel={`Quantity on line ${index + 1}`}
                  value={row.requestedQuantity}
                  onChange={(next) => update(index, { requestedQuantity: next ?? 0 })}
                  min={1}
                  disabled={saving}
                />

                <TdInput
                  id={`${ID_PREFIX}-items.${index}.remarks`}
                  ariaLabel={`Remarks on line ${index + 1}`}
                  value={row.remarks ?? ''}
                  onChange={(next) => update(index, { remarks: next || null })}
                  placeholder="optional"
                  disabled={saving}
                />
              </InlineRow>
            );
          })}
        </InlineAddTable>

        {/*
          PURPOSE and NOTES are FULL WIDTH, outside the grid — the remarks rule. Each is the
          longest value on the form and the one most likely to be a sentence, so half a row is
          the box that truncates while the field beside it needed none of the room.
        */}
        <Textarea
          id={`${ID_PREFIX}-purpose`}
          label="Purpose"
          required
          description="Why the office needs these supplies now. It is what an approver reads first."
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          error={purpose.trim() ? error?.fieldError('purpose') : 'Say why the office needs these.'}
          disabled={saving}
        />

        <Textarea
          id={`${ID_PREFIX}-notes`}
          label="Notes"
          description="Anything else the store should know."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          error={error?.fieldError('notes')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
